import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { navLinks, companyInfo } from '../data/mock';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Menu, Phone } from 'lucide-react';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (link) => {
    setIsOpen(false);
    
    // If it's a page link (like /spares), navigate to that page
    if (link.isPage) {
      navigate(link.href);
      return;
    }
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/' + link.href);
      return;
    }
    
    // Scroll to section on home page
    const element = document.querySelector(link.href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl shadow-lg shadow-blue-900/5 py-3'
          : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img 
              src="/images/logo.png" 
              alt="Shree Silos Group" 
              className="h-12 sm:h-14 md:h-16 lg:h-[72px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" role="navigation" aria-label="Main navigation">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => handleNavClick(link)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#1a4db3] ${
                  isScrolled
                    ? 'text-[#4a5568] hover:text-[#1a4db3] hover:bg-blue-50'
                    : 'text-[#4a5568] hover:text-[#1a4db3] hover:bg-blue-50/50'
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center gap-4">
            <Button
              onClick={() => handleNavClick({ href: '#contact' })}
              className="bg-[#1a4db3] hover:bg-[#1f87ff] text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/25"
              data-testid="get-quote-btn"
            >
              <Phone className="w-4 h-4 mr-2" />
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button
                variant="ghost"
                size="icon"
                className="text-[#1a4db3]"
                aria-label="Open navigation menu"
              >
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-white border-gray-200">
              <div className="flex flex-col h-full pt-8">
                <div className="flex items-center gap-3 mb-8">
                  <img 
                    src="/images/logo.png" 
                    alt="Shree Silos Group" 
                    className="h-10 w-auto object-contain"
                  />
                </div>
                <nav className="flex flex-col gap-2">
                  {navLinks.map((link) => (
                    <button
                      key={link.name}
                      onClick={() => handleNavClick(link)}
                      className="px-4 py-3 text-left text-[#4a5568] hover:text-[#1a4db3] hover:bg-blue-50 rounded-lg transition-all duration-300"
                    >
                      {link.name}
                    </button>
                  ))}
                </nav>
                <div className="mt-auto pb-8">
                  <Button
                    onClick={() => handleNavClick({ href: '#contact' })}
                    className="w-full bg-[#1a4db3] hover:bg-[#1f87ff] text-white font-semibold py-3"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Get Quote
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
